import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { defaultStorage } from 'aws-amplify/utils';
import logger from '../utils/logger';

/**
 * 必須環境変数を取得し、未設定の場合はエラーを投げる
 * セキュリティリスク防止のため、ハードコードされたフォールバック値は使用しない
 */
const getRequiredEnvVar = (name: string): string => {
  const value = process.env[name];
  if (!value) {
    const errorMessage = `必須環境変数 ${name} が設定されていません。ビルド時に正しい環境変数が設定されていることを確認してください。`;
    logger.error(`[AmplifyConfig] ${errorMessage}`);
    throw new Error(errorMessage);
  }
  return value;
};

// Amplify v6 configuration format
// セキュリティ: 環境変数未設定時はエラーを投げる（フォールバック値なし）
const amplifyConfig = {
  Auth: {
    Cognito: {
      userPoolId: getRequiredEnvVar('REACT_APP_USER_POOL_ID'),
      userPoolClientId: getRequiredEnvVar('REACT_APP_USER_POOL_CLIENT_ID'),
      identityPoolId: getRequiredEnvVar('REACT_APP_IDENTITY_POOL_ID'),
      loginWith: {
        email: true,
      },
      signUpVerificationMethod: 'code' as const,
      // パスワードフォーマット (Cognito 側ポリシーに合わせる: 8文字 + 数字必須)
      passwordFormat: {
        minLength: 8,
        requireLowercase: false,
        requireUppercase: false,
        requireNumbers: true,
        requireSpecialCharacters: false,
      },
    },
  },
};

// Guard against double initialization (StrictMode protection)
let isConfigured = false;

export const configureAmplify = () => {
  if (isConfigured) {
    logger.debug('🔄 [AmplifyConfig] Already configured, skipping...');
    return;
  }

  logger.info('🚀 [AmplifyConfig] Configuring Amplify v6 with:', {
    userPoolId: amplifyConfig.Auth.Cognito.userPoolId,
    userPoolClientId: amplifyConfig.Auth.Cognito.userPoolClientId,
    identityPoolId: amplifyConfig.Auth.Cognito.identityPoolId,
  });

  Amplify.configure(amplifyConfig);

  // localStorage を使い、タブ/ブラウザを閉じてもセッションを保持する (#101)
  // setKeyValueStorage が将来の Amplify 更新で throw してもブートを止めない。
  // Amplify の無指定既定も localStorage なので、失敗時はそれに委ねて続行する (#108)
  try {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage);
    logger.debug('🗂️ [AmplifyConfig] Token storage set to localStorage (defaultStorage)');
  } catch (err) {
    logger.warn('⚠️ [AmplifyConfig] Failed to set localStorage; continuing with Amplify default (localStorage)', err);
  }

  isConfigured = true;
  logger.info('✅ [AmplifyConfig] Amplify v6 configuration complete');
};

export default amplifyConfig;
